import React from 'react'

import { SxProps } from '@mui/material'

import { useAnalysisActions } from 'redux/actions/Analyses'
import { useAnalysis } from 'redux/hooks/Analyses'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'

import ContentBox from 'components_new/molecules/ContentBox'
import ValueText from 'components_new/molecules/ValueText'

import SelectUser from 'components_new/organisms/SelectUser'

import { getParsedDate } from 'utils/dateParser'

interface AnalysisItemProps {
  collapsed?: boolean
  disableBorder?: boolean
  disablePadding?: boolean
  id: string | null
  onExpand?: () => void
  /** Called along with the "Gå till" navigation, e.g. to close a dialog. */
  onGoTo?: () => void
  sx?: SxProps
}

const AnalysisItem = (props: AnalysisItemProps) => {
  const { collapsed, disableBorder, disablePadding, id, onExpand, onGoTo, sx } =
    props

  const { analysis } = useAnalysis(id)
  const { tryUpdateAnalysis } = useAnalysisActions()
  const { lang, t } = useTranslation()

  if (!analysis) return null

  const selectedProperties = analysis.qualified_properties.filter(
    (item) => item.selected
  )

  return (
    <>
      <ContentBox
        border={!disableBorder}
        collapse={{
          active: collapsed !== undefined,
          collapsed: collapsed,
          defaultCollapsed: collapsed,
          onExpand: onExpand
        }}
        disablePadding={disablePadding}
        header={{
          actions: (
            <ValueText
              color="success.text"
              displayPositiveIndicator={true}
              unit={'kr/år'}
              value={analysis.potential}
            />
          ),
          icon: { color: 'success', name: 'Analysis' },
          title: analysis.title
        }}
        key={analysis.id}
        sx={sx}
      >
        {analysis.description ? (
          <Text
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              mb: 1,
              overflow: 'hidden',
              WebkitLineClamp: '3',
              WebkitBoxOrient: 'vertical'
            }}
            variant="body2"
          >
            {analysis.description}
          </Text>
        ) : null}

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedProperties.length === 0 ? null : (
              <Chip
                label={
                  selectedProperties.length > 1
                    ? `${selectedProperties.length} ${t(
                      'org_units',
                      'units',
                      true
                    )}`
                    : selectedProperties[0].name
                }
                size="xsmall"
              />
            )}
          </Box>

          <SelectUser
            onChange={(value) =>
              tryUpdateAnalysis(analysis.id, { responsible_account_id: value })
            }
            size="xsmall"
            value={analysis.responsible_account_id}
          />
        </Box>

        <Text
          color="text.secondary"
          component="div"
          sx={{ mb: 1 }}
          variant="caption"
        >
          {`${t('global', 'generated')} ${getParsedDate(
            analysis.created_at,
            lang,
            { time: 'full' }
          )}`}
        </Text>

        <Button
          fullWidth={true}
          href={`/analyses/${analysis.id}`}
          onClick={onGoTo}
          size="small"
        >
          {t('global', 'go_to')}
        </Button>
      </ContentBox>
    </>
  )
}

export default AnalysisItem
